import { compactKeywordUsePoliciesForStep2 } from './keyword-strategy-request.js';

export function removeConflictingKeywords(selectedKeywords, conflicts) {
  const selected = new Set(selectedKeywords || []);
  const conflictSet = new Set((Array.isArray(conflicts) ? conflicts : [])
    .map((item) => String(item?.keyword || '').trim().toLowerCase())
    .filter(Boolean));
  return new Set([...selected].filter((keyword) => !conflictSet.has(String(keyword || '').trim().toLowerCase())));
}

export function resolveKeywordConflicts(selectedKeywords, conflicts) {
  const removedConflicts = (Array.isArray(conflicts) ? conflicts : [])
    .filter((item) => String(item?.keyword || '').trim());
  const selected = removeConflictingKeywords(selectedKeywords, removedConflicts);

  return {
    selected,
    removedConflicts,
    result: {
      hasConflict: false,
      conflicts: [],
      removedConflicts,
      checkStatus: 'completed_with_removals',
      decision: 'passed',
      canProceed: true,
      degraded: false,
      reason: `已自动移除 ${removedConflicts.length} 个与产品卖点冲突的关键词。`,
    },
  };
}

function keywordPrecheckSignature(keywords = []) {
  const values = keywords instanceof Set ? [...keywords] : (Array.isArray(keywords) ? keywords : []);
  return [...new Set(values
    .map((item) => String(item || '').trim().toLowerCase())
    .filter(Boolean))]
    .sort()
    .join('|');
}

export function createKeywordPrecheckController({
  select,
  getState,
  setState,
  preCheckKeywords,
  getErrorMessage,
  clientDebug,
  updateStOutput,
  showConflictModal,
  showReviewModal,
  closeReviewModal,
  showModalStatus,
  hideModalStatus,
  closeModalDialog,
  warn = (...args) => console.warn(...args),
  setTimeoutFn = globalThis.setTimeout,
} = {}) {
  let selectionSignature = '';

  function getSelectedKeywords() {
    const selected = getState('selectedStKeywords');
    return selected instanceof Set ? [...selected] : (Array.isArray(selected) ? selected : []);
  }

  function setNextButtonLoading(button, isLoading) {
    if (!button) return;
    if (isLoading) {
      button.dataset.originalText = button.textContent;
      button.disabled = true;
      button.textContent = '正在检测关键词...';
      return;
    }
    button.disabled = false;
    if (button.dataset.originalText) {
      button.textContent = button.dataset.originalText;
      delete button.dataset.originalText;
    }
  }

  function showProceedModal(result = {}) {
    const modal = select('#keyword-proceed-modal');
    if (!modal) return;
    const title = select('#keyword-proceed-title');
    const message = select('#keyword-proceed-message');
    const degraded = Boolean(result?.degraded);
    if (title) title.textContent = degraded ? '关键词检测降级继续' : '关键词检测通过';
    if (message) {
      if (degraded) {
        message.textContent = result?.warning
          || '关键词智能终审未完成，已按当前已选关键词继续。请确认不存在与产品事实直接矛盾的词。';
        showModalStatus(modal);
        return;
      }
      const source = result?.factsSourceLabel || '产品卖点';
      message.textContent = `已基于${source}完成互斥检测，正在前往 Listing 生成。`;
    }
    showModalStatus(modal);
  }

  function hideProceedModal() {
    const modal = select('#keyword-proceed-modal');
    if (modal) hideModalStatus(modal);
  }

  function showCheckingModal(productInfo = {}) {
    const modal = select('#keyword-checking-modal');
    if (!modal) return;
    const message = select('#keyword-checking-message');
    const explicitItems = Array.isArray(productInfo?.sellingPointItems)
      && productInfo.sellingPointItems.some((item) => String(item || '').trim());
    const usesUserSellingPoints = explicitItems
      || (String(productInfo?.sellingPoints || '').trim()
        && productInfo?.sellingPointsSource !== 'competitor_inferred');
    if (message) {
      message.textContent = usesUserSellingPoints
        ? '正在使用你填写的产品卖点核对已选关键词，请稍候。'
        : '未填写明确卖点，正在从 Step01 竞品 ASIN 详情提炼产品事实并检测，请稍候。';
    }
    showModalStatus(modal);
  }

  function hideCheckingModal() {
    const modal = select('#keyword-checking-modal');
    if (modal) hideModalStatus(modal);
  }

  function hideReviewModal() {
    const modal = select('#keyword-review-modal');
    if (modal) closeModalDialog(modal);
  }

  function storeResult(result) {
    const normalized = result && typeof result === 'object' ? result : {
      hasConflict: false,
      conflicts: [],
      checkStatus: 'request_failed',
      decision: 'review_required',
      canProceed: false,
      degraded: true,
      warning: '关键词检测没有返回有效结果，请重试。',
    };
    setState('keywordConflictResult', normalized);
    setState('lastDetectedConflicts', Array.isArray(normalized.conflicts) ? normalized.conflicts : []);
    const batchJob = getState('batchJob');
    if (batchJob && typeof batchJob === 'object') {
      setState('batchJob', {
        ...batchJob,
        keywordConflicts: Array.isArray(normalized.conflicts) ? normalized.conflicts : [],
      });
    }
    if (normalized.checkStatus !== 'stale') {
      selectionSignature = keywordPrecheckSignature(getState('selectedStKeywords'));
    }
    updateStOutput();
    return normalized;
  }

  function invalidateForSelectionChange() {
    const currentResult = getState('keywordConflictResult');
    if (!currentResult || currentResult.checkStatus === 'stale') return;
    const currentSignature = keywordPrecheckSignature(getState('selectedStKeywords'));
    if (!selectionSignature || currentSignature === selectionSignature) return;

    storeResult({
      hasConflict: false,
      conflicts: [],
      checkStatus: 'stale',
      decision: 'review_required',
      canProceed: false,
      degraded: false,
      warning: '关键词选择已变化，请重新检测后再进入 Listing 生成。',
    });
  }

  function resolveDecision(result) {
    if (result?.hasConflict && Array.isArray(result.conflicts) && result.conflicts.length > 0) return 'blocked';
    if (result?.decision === 'blocked' || result?.decision === 'review_required') return result.decision;
    if (result?.canProceed === false) return 'review_required';
    return 'passed';
  }

  function buildRequestFailure(error) {
    return {
      hasConflict: false,
      conflicts: [],
      checkStatus: 'request_failed',
      decision: 'review_required',
      canProceed: false,
      degraded: true,
      warning: getErrorMessage(error) || '关键词检测请求失败，请重试。',
      errorCode: 'PRECHECK_REQUEST_FAILED',
    };
  }

  async function run() {
    const selectedKeywords = getSelectedKeywords();
    if (selectedKeywords.length === 0) {
      return storeResult({
        hasConflict: false,
        conflicts: [],
        checkStatus: 'skipped',
        decision: 'passed',
        canProceed: true,
        degraded: false,
        reason: '未选择关键词，无需执行互斥检测。',
      });
    }

    const productInfo = getState('productInfo') || {};
    const sellingPoints = productInfo.sellingPoints || '';
    const batchJobId = getState('batchJobId') || '';
    const snapshots = Array.isArray(getState('batchSnapshots'))
      ? getState('batchSnapshots').filter((item) => item?.status === 'completed' && item?.asin)
      : [];
    const included = getState('competitorAsinsIncluded');
    const snapshotAsins = snapshots.length > 1
      ? snapshots
          .map((item) => item.asin)
          .filter((asin) => !(included instanceof Set) || included.size === 0 || included.has(asin))
      : undefined;
    clientDebug('[Pre-Check] 已选关键词数量:', selectedKeywords.length);
    try {
      const result = await preCheckKeywords(sellingPoints, selectedKeywords, batchJobId, {
        keywordUsePolicies: compactKeywordUsePoliciesForStep2(getState('keywordUsePolicies') || []),
        competitorRoles: getState('competitorRoles') || [],
        ...(snapshotAsins?.length ? { snapshotAsins } : {}),
      });
      clientDebug('[Pre-Check] 检测状态:', result?.checkStatus || 'unknown');
      return storeResult(result);
    } catch (error) {
      warn('[Step2] preCheckKeywords failed:', error?.message || error);
      return storeResult(buildRequestFailure(error));
    }
  }

  function proceed({ showSuccess = false, result = {} } = {}) {
    if (!showSuccess) {
      setState('currentStep', 3);
      return;
    }
    showProceedModal(result);
    setTimeoutFn(() => {
      hideProceedModal();
      setState('currentStep', 3);
    }, 900);
  }

  function handleOutcome(result, { trigger } = {}) {
    const decision = resolveDecision(result);
    if (decision === 'blocked') {
      const resolved = resolveKeywordConflicts(getState('selectedStKeywords') || [], result.conflicts || []);
      setState('selectedStKeywords', resolved.selected);
      storeResult(resolved.result);
      showConflictModal(resolved.removedConflicts, result, trigger);
      return;
    }
    if (decision === 'review_required') {
      showReviewModal(result, trigger);
      return;
    }
    proceed({ showSuccess: true, result });
  }

  async function start(nextButton = select('#step2-next-button')) {
    showCheckingModal(getState('productInfo') || {});
    setNextButtonLoading(nextButton, true);
    try {
      const result = await run();
      handleOutcome(result, { trigger: nextButton });
    } finally {
      hideCheckingModal();
      setNextButtonLoading(nextButton, false);
    }
  }

  return {
    invalidateForSelectionChange,
    reset: () => { selectionSignature = ''; },
    hideReviewModal,
    proceed,
    start,
  };
}
